/**
 * Guided tour overlay (doc 147 §9 Phase 6).
 *
 * A 6-step orientation card for new joiners. Presentational — the step index
 * and the navigation callbacks are injected; `App` owns the state and the
 * seen-flag persistence.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import { TOUR_STEPS, isLastStep } from '../state/tour.js';
import { manifestSummary } from '../state/selectors.js';
import { navigate } from '../state/hashRouter.js';
import { Icon } from '../components/Icon.js';

export interface GuidedTourProps {
  stepIndex: number;
  onNext: () => void;
  onPrev: () => void;
  /** Skip or finish — both dismiss the tour. */
  onClose: () => void;
  manifest: AtlasManifest | null;
}

export function GuidedTour({
  stepIndex,
  onNext,
  onPrev,
  onClose,
  manifest,
}: GuidedTourProps): React.JSX.Element | null {
  const step = TOUR_STEPS[stepIndex];
  if (!step) return null;
  const last = isLastStep(stepIndex);

  const advance = (): void => {
    if (last) {
      onClose();
      return;
    }
    const next = TOUR_STEPS[stepIndex + 1];
    if (next) navigate(next.goTo);
    onNext();
  };

  // Substitute manifest summary tokens in the body text
  let displayBody = step.body;
  if (manifest && step.id === 'welcome') {
    const summary = manifestSummary(manifest);
    displayBody = step.body
      .replace('{{ENTITY_COUNT}}', String(summary.entityCount))
      .replace('{{DOMAIN_COUNT}}', String(summary.domainCount));
  } else if (!manifest && step.id === 'welcome') {
    // Fallback when manifest is not yet loaded
    displayBody = step.body
      .replace('{{ENTITY_COUNT}}', 'many')
      .replace('{{DOMAIN_COUNT}}', 'many');
  }

  return (
    <div className="tour-backdrop" role="presentation">
      <div className="tour-card" role="dialog" aria-modal="true" aria-label="Atlas guided tour">
        <div className="tour-progress mono">
          Step {stepIndex + 1} of {TOUR_STEPS.length}
        </div>
        <h2 className="tour-title">{step.title}</h2>
        <p className="tour-body">{displayBody}</p>
        <div className="tour-actions">
          <button className="btn" onClick={onClose}>
            Skip tour
          </button>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', marginLeft: 'auto' }}>
            <button
              className="btn btn-ghost"
              onClick={onPrev}
              disabled={stepIndex === 0}
            >
              <Icon name="arrow_back" size="sm" />
              Back
            </button>
            <button className="btn btn-primary" onClick={advance}>
              {last ? 'Finish' : 'Next'}
              {!last && <Icon name="arrow_forward" size="sm" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
